import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, FileUp, FileVideo, Upload, X } from 'lucide-react';
import {
  api,
  type AssignedRole,
  type ImportConfirmRow,
  type ImportMatchedRow,
  type ImportPreviewResponse,
  type ImportUnmatchedRow,
  type ImportVideoJobState,
  type OcrImportPayload,
  type Player,
  type SlotKey,
} from '@/lib/api';

interface Props {
  eventId: number;
  open: boolean;
  onClose: () => void;
  onImported: () => void;
  initialVideoJobId?: string | null;
}

type Resolution =
  | { kind: 'matched'; ocrName: string; row: ImportMatchedRow; included: boolean }
  | { kind: 'unmatched'; ocrName: string; row: ImportUnmatchedRow; playerId: number | null; included: boolean };

function assignmentLabel(role: AssignedRole | null, slot: SlotKey | null) {
  if (role && slot) return `${role === 'starter' ? 'Starter' : 'Sub'} ${slot}`;
  return '—';
}

function previousAssignmentLabel(
  appliedA: boolean,
  appliedB: boolean,
  assignedSlot: SlotKey | null,
  assignedRole: AssignedRole | null,
) {
  if (assignedRole && assignedSlot) return `${assignedRole === 'starter' ? 'Starter' : 'Sub'} ${assignedSlot}`;
  if (appliedA && appliedB) return 'applied A+B';
  if (appliedA) return 'applied A';
  if (appliedB) return 'applied B';
  return '—';
}

type UploadMode = 'video' | 'json';

export default function ImportSignupsDialog({
  eventId,
  open,
  onClose,
  onImported,
  initialVideoJobId = null,
}: Props) {
  const [mode, setMode] = useState<UploadMode>('video');
  const [payload, setPayload] = useState<OcrImportPayload | null>(null);
  const [payloadFile, setPayloadFile] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportPreviewResponse | null>(null);
  const [resolutions, setResolutions] = useState<Resolution[]>([]);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoSlot, setVideoSlot] = useState<SlotKey>('A');
  const [videoJobId, setVideoJobId] = useState<string | null>(initialVideoJobId);
  const playersQuery = useQuery({
    queryKey: ['players', 'all-for-import'],
    queryFn: () => api.listPlayers({ includeInactive: true }),
    enabled: open,
  });

  const previewMutation = useMutation({
    mutationFn: (p: OcrImportPayload) => api.importSignupsPreview(eventId, p),
    onSuccess: (data) => {
      setPreview(data);
      setResolutions([
        ...data.matched.map<Resolution>((row) => ({ kind: 'matched', ocrName: row.ocrName, row, included: true })),
        ...data.unmatched.map<Resolution>((row) => ({
          kind: 'unmatched',
          ocrName: row.ocrName,
          row,
          playerId: row.suggestions[0]?.playerId ?? null,
          included: false,
        })),
      ]);
    },
  });

  const confirmMutation = useMutation({
    mutationFn: async (rows: ImportConfirmRow[]) => {
      if (!preview) throw new Error('No preview loaded');
      // Send the video-job id alongside so the server can stamp
      // appliedAt on it — same pattern as the VS import.
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (videoJobId) headers['x-event-import-job-id'] = videoJobId;
      const res = await fetch(`/api/events/${eventId}/signups/import/confirm`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({ slot: preview.slotInfo.inputSlot, rows }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? res.statusText);
      return res.json();
    },
    onSuccess: () => {
      onImported();
      reset();
      onClose();
    },
  });

  const videoUploadMutation = useMutation({
    mutationFn: (input: { file: File; slot: SlotKey }) =>
      api.uploadSignupsVideo(eventId, input.slot, input.file),
    onSuccess: (data) => setVideoJobId(data.jobId),
  });

  const videoRetryMutation = useMutation({
    mutationFn: () => api.retryEventImportJob(eventId, videoJobId!),
  });

  const videoJobQuery = useQuery({
    queryKey: ['import-video-job', eventId, videoJobId],
    queryFn: () => api.getImportVideoJob(eventId, videoJobId!),
    enabled: !!videoJobId,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === 'queued' || s === 'running' ? 2000 : false;
    },
  });

  // When the job completes, hand its payload off to the preview flow —
  // same downstream UI as the JSON path.
  useEffect(() => {
    if (videoJobQuery.data?.status === 'done' && videoJobQuery.data.payload && !preview) {
      previewMutation.mutate(videoJobQuery.data.payload);
    }
  }, [videoJobQuery.data, preview, previewMutation]);

  function reset() {
    setPayload(null);
    setPayloadFile(null);
    setParseError(null);
    setPreview(null);
    setResolutions([]);
    setVideoFile(null);
    setVideoJobId(null);
    previewMutation.reset();
    confirmMutation.reset();
    videoUploadMutation.reset();
  }

  function onFile(f: File) {
    setParseError(null);
    setPreview(null);
    setResolutions([]);
    const reader = new FileReader();
    reader.onerror = () => setParseError('Failed to read file');
    reader.onload = () => {
      try {
        const obj = JSON.parse(String(reader.result));
        if (!obj || typeof obj !== 'object' || !('signups' in obj) || !('slot' in obj)) {
          setParseError('JSON does not look like an lw-import payload (missing "slot" or "signups")');
          return;
        }
        setPayload(obj as OcrImportPayload);
        setPayloadFile(f.name);
      } catch (e) {
        setParseError(`Could not parse JSON: ${(e as Error).message}`);
      }
    };
    reader.readAsText(f);
  }

  function setUnmatchedPlayer(ocrName: string, playerId: number | null) {
    setResolutions((rs) =>
      rs.map((r) =>
        r.kind === 'unmatched' && r.ocrName === ocrName
          ? { ...r, playerId, included: playerId != null }
          : r,
      ),
    );
  }

  function toggleInclude(ocrName: string, included: boolean) {
    setResolutions((rs) => rs.map((r) => (r.ocrName === ocrName ? { ...r, included } : r)));
  }

  const confirmRows = useMemo<ImportConfirmRow[]>(() => {
    return resolutions
      .filter((r) => r.included)
      .map((r) => {
        if (r.kind === 'matched') {
          return {
            ocrName: r.ocrName,
            playerId: r.row.player.id,
            role: r.row.role,
            assignmentSlot: r.row.assignmentSlot,
          };
        }
        return {
          ocrName: r.ocrName,
          playerId: r.playerId!,
          role: r.row.role,
          assignmentSlot: r.row.assignmentSlot,
        };
      });
  }, [resolutions]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-slate-950/80 p-4 sm:p-8">
      <div className="w-full max-w-5xl rounded-lg border border-slate-800 bg-slate-900 shadow-xl">
        <header className="flex items-center justify-between border-b border-slate-800 px-5 py-3">
          <h2 className="text-base font-semibold">Import signups from screen recording</h2>
          <button
            onClick={() => {
              reset();
              onClose();
            }}
            className="rounded p-1 text-slate-400 hover:bg-slate-800 hover:text-white"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </header>

        <div className="space-y-5 p-5">
          {!preview && (
            <section className="space-y-3">
              <div className="flex gap-1 border-b border-slate-800">
                {(['video', 'json'] as const).map((m) => (
                  <button
                    key={m}
                    onClick={() => setMode(m)}
                    className={
                      'px-3 py-1.5 text-sm border-b-2 -mb-px ' +
                      (mode === m
                        ? 'border-indigo-500 text-white'
                        : 'border-transparent text-slate-400 hover:text-slate-200')
                    }
                  >
                    {m === 'video' ? 'Upload video' : 'Upload JSON (CLI)'}
                  </button>
                ))}
              </div>

              {mode === 'video' ? (
                <VideoTab
                  slot={videoSlot}
                  onSlotChange={setVideoSlot}
                  file={videoFile}
                  onFileChange={setVideoFile}
                  job={videoJobQuery.data}
                  jobLoading={!!videoJobId && !videoJobQuery.data}
                  uploading={videoUploadMutation.isPending}
                  uploadError={(videoUploadMutation.error as Error | null)?.message ?? null}
                  onStart={() =>
                    videoFile && videoUploadMutation.mutate({ file: videoFile, slot: videoSlot })
                  }
                  onRetry={() =>
                    videoRetryMutation.mutate(undefined, {
                      onSuccess: () => videoJobQuery.refetch(),
                    })
                  }
                  retrying={videoRetryMutation.isPending}
                  retryError={(videoRetryMutation.error as Error | null)?.message ?? null}
                  onCancel={reset}
                />
              ) : (
                <>
                  <div className="text-sm text-slate-300">
                    <p>
                      Generate a JSON file via the <code className="rounded bg-slate-800 px-1 text-xs">lw-import</code> CLI:
                    </p>
                    <pre className="mt-1 overflow-x-auto rounded bg-slate-950 p-2 text-xs text-slate-200">
{`tools/lw-import/.venv/bin/python tools/lw-import/lw_import.py ${eventId} <A|B> <video.mov> --output signups.json --dry-run`}
                    </pre>
                    <p className="mt-2 text-xs text-slate-500">
                      Then drop the resulting <code>signups.json</code> here.
                    </p>
                  </div>

                  <label
                    className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded border-2 border-dashed border-slate-700 px-6 py-10 hover:border-slate-500"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const f = e.dataTransfer.files?.[0];
                      if (f) onFile(f);
                    }}
                  >
                    <FileUp size={28} className="text-slate-500" />
                    <span className="text-sm text-slate-400">
                      Drop signups.json here, or click to pick a file
                    </span>
                    <input
                      type="file"
                      accept="application/json,.json"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) onFile(f);
                      }}
                    />
                    {payloadFile && (
                      <span className="rounded bg-slate-800 px-2 py-1 text-xs text-slate-300">
                        {payloadFile} — {payload?.signups.length ?? 0} rows, slot {payload?.slot}
                      </span>
                    )}
                  </label>

                  {parseError && (
                    <div className="rounded bg-red-900/30 px-3 py-2 text-sm text-red-200">{parseError}</div>
                  )}

                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => reset()}
                      className="rounded border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
                      disabled={!payload}
                    >
                      Clear
                    </button>
                    <button
                      onClick={() => payload && previewMutation.mutate(payload)}
                      disabled={!payload || previewMutation.isPending}
                      className="rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
                    >
                      {previewMutation.isPending ? 'Analyzing…' : 'Preview'}
                    </button>
                  </div>
                </>
              )}

              {previewMutation.isError && (
                <div className="rounded bg-red-900/30 px-3 py-2 text-sm text-red-200">
                  {(previewMutation.error as Error)?.message ?? 'Preview failed'}
                </div>
              )}
            </section>
          )}

          {preview && (
            <PreviewView
              preview={preview}
              resolutions={resolutions}
              players={playersQuery.data ?? []}
              onSetUnmatched={setUnmatchedPlayer}
              onToggleInclude={toggleInclude}
              confirmRows={confirmRows}
              onBack={reset}
              onConfirm={() => confirmMutation.mutate(confirmRows)}
              confirming={confirmMutation.isPending}
              error={(confirmMutation.error as Error | null)?.message ?? null}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewView({
  preview,
  resolutions,
  players,
  onSetUnmatched,
  onToggleInclude,
  confirmRows,
  onBack,
  onConfirm,
  confirming,
  error,
}: {
  preview: ImportPreviewResponse;
  resolutions: Resolution[];
  players: Player[];
  onSetUnmatched: (ocrName: string, playerId: number | null) => void;
  onToggleInclude: (ocrName: string, included: boolean) => void;
  confirmRows: ImportConfirmRow[];
  onBack: () => void;
  onConfirm: () => void;
  confirming: boolean;
  error: string | null;
}) {
  const matched = resolutions.filter((r): r is Extract<Resolution, { kind: 'matched' }> => r.kind === 'matched');
  const unmatched = resolutions.filter((r): r is Extract<Resolution, { kind: 'unmatched' }> => r.kind === 'unmatched');

  const sortedPlayers = useMemo(
    () => [...players].sort((a, b) => a.name.localeCompare(b.name)),
    [players],
  );

  const usedPlayerIds = useMemo(() => {
    const set = new Set<number>();
    for (const r of matched) set.add(r.row.player.id);
    for (const r of unmatched) if (r.playerId) set.add(r.playerId);
    return set;
  }, [matched, unmatched]);

  return (
    <div className="space-y-5">
      <header className="rounded border border-slate-800 bg-slate-950/40 p-3 text-sm">
        <div className="flex flex-wrap items-center gap-3">
          <strong>{preview.event.eventDate}</strong>
          <span className="text-slate-400">{preview.event.type.replace('_', ' ')}</span>
          <span className="text-slate-400">·</span>
          <span>
            Slot <strong>{preview.slotInfo.inputSlot}</strong>
            {preview.slotInfo.detectedBadge && preview.slotInfo.detectedBadge !== preview.slotInfo.inputSlot && (
              <span className="ml-1 text-amber-400">(video badge: {preview.slotInfo.detectedBadge})</span>
            )}
          </span>
          {preview.slotInfo.sharedTime && (
            <span className="rounded bg-amber-900/40 px-2 py-0.5 text-xs text-amber-200">
              shared time — applied_slot will be NULL
            </span>
          )}
        </div>
        <div className="mt-1 text-xs text-slate-500">
          {preview.counts.ocrRows} rows · {preview.counts.matched} matched · {preview.counts.unmatched} unmatched · {preview.counts.conflicts} conflicts
        </div>
        {preview.fightTimeWarning && (
          <div className="mt-2 flex items-center gap-2 rounded bg-amber-900/30 px-2 py-1 text-xs text-amber-200">
            <AlertTriangle size={14} /> {preview.fightTimeWarning}
          </div>
        )}
      </header>

      {preview.conflicts.length > 0 && <ConflictsList conflicts={preview.conflicts} />}

      <section>
        <h3 className="mb-2 text-sm font-semibold text-emerald-300">Matched ({matched.length})</h3>
        <div className="overflow-hidden rounded border border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/40 text-xs uppercase text-slate-400">
              <tr>
                <th className="px-2 py-1 text-left">Include</th>
                <th className="px-2 py-1 text-left">OCR name</th>
                <th className="px-2 py-1 text-left">Player</th>
                <th className="px-2 py-1 text-left">Assignment (OCR)</th>
                <th className="px-2 py-1 text-left">Previous</th>
                <th className="px-2 py-1 text-left">Match</th>
              </tr>
            </thead>
            <tbody>
              {matched.map((r) => (
                <tr key={r.ocrName} className="border-t border-slate-900">
                  <td className="px-2 py-1">
                    <input
                      type="checkbox"
                      checked={r.included}
                      onChange={(e) => onToggleInclude(r.ocrName, e.target.checked)}
                    />
                  </td>
                  <td className="px-2 py-1">{r.ocrName}</td>
                  <td className="px-2 py-1">
                    {r.row.player.name}
                    {r.row.player.displayName && (
                      <span className="ml-1 text-xs text-slate-500">({r.row.player.displayName})</span>
                    )}
                    {!r.row.player.active && (
                      <span className="ml-2 rounded bg-amber-900/30 px-1 text-xs text-amber-300">inactive</span>
                    )}
                  </td>
                  <td className="px-2 py-1 text-xs">
                    {assignmentLabel(r.row.role, r.row.assignmentSlot)}
                  </td>
                  <td className="px-2 py-1 text-xs text-slate-400">
                    {previousAssignmentLabel(
                      r.row.previousAppliedA,
                      r.row.previousAppliedB,
                      r.row.previousAssignedSlot,
                      r.row.previousAssignedRole,
                    )}
                  </td>
                  <td className="px-2 py-1 text-xs text-slate-500">
                    {r.row.matchKind === 'exact_name' ? 'exact' : r.row.matchKind === 'exact_display' ? 'exact (display)' : `fuzzy ${(r.row.matchScore * 100).toFixed(0)}%`}
                    {r.row.alreadyApplied && (
                      <span className="ml-1 rounded bg-indigo-900/40 px-1 text-indigo-200">re-import</span>
                    )}
                  </td>
                </tr>
              ))}
              {matched.length === 0 && (
                <tr><td colSpan={6} className="px-2 py-3 text-center text-slate-500">No matches</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold text-amber-300">Unmatched ({unmatched.length})</h3>
        <div className="overflow-hidden rounded border border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/40 text-xs uppercase text-slate-400">
              <tr>
                <th className="px-2 py-1 text-left">Include</th>
                <th className="px-2 py-1 text-left">OCR name</th>
                <th className="px-2 py-1 text-left">Map to player…</th>
                <th className="px-2 py-1 text-left">Assignment (OCR)</th>
              </tr>
            </thead>
            <tbody>
              {unmatched.map((r) => (
                <tr key={r.ocrName} className="border-t border-slate-900">
                  <td className="px-2 py-1">
                    <input
                      type="checkbox"
                      checked={r.included}
                      disabled={!r.playerId}
                      onChange={(e) => onToggleInclude(r.ocrName, e.target.checked)}
                    />
                  </td>
                  <td className="px-2 py-1">{r.ocrName}</td>
                  <td className="px-2 py-1">
                    <select
                      className="w-full rounded bg-slate-800 px-2 py-1 text-sm"
                      value={r.playerId ?? ''}
                      onChange={(e) => onSetUnmatched(r.ocrName, e.target.value ? Number(e.target.value) : null)}
                    >
                      <option value="">— pick a player —</option>
                      {r.row.suggestions.length > 0 && (
                        <optgroup label="Suggestions">
                          {r.row.suggestions.map((s) => (
                            <option key={s.playerId} value={s.playerId}>
                              {s.name} ({(s.score * 100).toFixed(0)}%)
                            </option>
                          ))}
                        </optgroup>
                      )}
                      <optgroup label="All players">
                        {sortedPlayers
                          .filter((p) => !usedPlayerIds.has(p.id) || p.id === r.playerId)
                          .map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                              {p.displayName ? ` (${p.displayName})` : ''}
                              {!p.active ? ' [inactive]' : ''}
                            </option>
                          ))}
                      </optgroup>
                    </select>
                  </td>
                  <td className="px-2 py-1 text-xs">
                    {assignmentLabel(r.row.role, r.row.assignmentSlot)}
                  </td>
                </tr>
              ))}
              {unmatched.length === 0 && (
                <tr><td colSpan={4} className="px-2 py-3 text-center text-slate-500">Everything matched</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <div className="flex items-center justify-between gap-3 border-t border-slate-800 pt-4">
        <button
          onClick={onBack}
          className="rounded border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
        >
          Back
        </button>
        <div className="text-sm text-slate-400">
          <strong>{confirmRows.length}</strong> rows will be applied
          {preview.slotInfo.sharedTime ? ' (no slot — shared time)' : ` to slot ${preview.slotInfo.inputSlot}`}
        </div>
        <button
          onClick={onConfirm}
          disabled={confirmRows.length === 0 || confirming}
          className="inline-flex items-center gap-1 rounded bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
        >
          {confirming ? <Upload size={14} className="animate-pulse" /> : <CheckCircle2 size={14} />}
          {confirming ? 'Importing…' : 'Confirm import'}
        </button>
      </div>
      {error && (
        <div className="rounded bg-red-900/30 px-3 py-2 text-sm text-red-200">{error}</div>
      )}
    </div>
  );
}

function VideoTab({
  slot,
  onSlotChange,
  file,
  onFileChange,
  job,
  jobLoading,
  uploading,
  uploadError,
  onStart,
  onRetry,
  retrying,
  retryError,
  onCancel,
}: {
  slot: SlotKey;
  onSlotChange: (s: SlotKey) => void;
  file: File | null;
  onFileChange: (f: File | null) => void;
  job: ImportVideoJobState | undefined;
  jobLoading: boolean;
  uploading: boolean;
  uploadError: string | null;
  onStart: () => void;
  onRetry: () => void;
  retrying: boolean;
  retryError: string | null;
  onCancel: () => void;
}) {
  const running = job?.status === 'queued' || job?.status === 'running';
  const failed = job?.status === 'failed';
  const succeeded = job?.status === 'done';
  const stage = job?.progress.stage ?? (uploading ? 'uploading' : 'queued');
  const stageLabel: Record<string, string> = {
    queued: 'Queued',
    uploading: 'Uploading video…',
    processing: 'Extracting roster from video…',
    done: 'Done',
  };
  const stagePct = stage === 'queued' ? 5 : stage === 'uploading' ? 35 : stage === 'processing' ? 75 : 100;

  function pickFile(picked: File | null | undefined) {
    if (!picked) return;
    if (!picked.type.startsWith('video/')) return;
    onFileChange(picked);
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-300">
        Record the in-game "Teilnehmer auswählen" list end-to-end (30-90&nbsp;s,
        pan slowly), then drop the video here. Extraction happens in the
        cloud — no OCR sidecar required.
      </p>

      <div className="flex items-center gap-3 text-sm">
        <span className="text-slate-400">Slot:</span>
        {(['A', 'B'] as const).map((s) => (
          <label key={s} className="flex items-center gap-1 cursor-pointer">
            <input
              type="radio"
              name="video-slot"
              checked={slot === s}
              onChange={() => onSlotChange(s)}
              disabled={running || succeeded}
            />
            <span>{s}</span>
          </label>
        ))}
      </div>

      <label
        className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded border-2 border-dashed border-slate-700 px-6 py-8 hover:border-slate-500"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          pickFile(e.dataTransfer.files?.[0]);
        }}
      >
        <FileVideo size={28} className="text-slate-500" />
        <span className="text-sm text-slate-400">
          Drop an .mp4 or .mov here, or click to pick a file
        </span>
        <input
          type="file"
          accept="video/mp4,video/quicktime,video/*"
          className="hidden"
          disabled={running || succeeded}
          onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
        />
        {file && (
          <span className="rounded bg-slate-800 px-2 py-1 text-xs text-slate-300">
            {file.name} — {(file.size / (1024 * 1024)).toFixed(1)} MB
          </span>
        )}
      </label>

      {uploadError && (
        <div className="rounded bg-red-900/30 px-3 py-2 text-sm text-red-200">{uploadError}</div>
      )}
      {failed && (
        <div className="space-y-2 rounded bg-red-900/30 px-3 py-2 text-sm text-red-200">
          <div>Video extraction failed: {job?.error}</div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={onRetry}
              disabled={retrying || !job?.videoAvailable}
              className="rounded bg-indigo-600 px-3 py-1 text-sm text-white disabled:opacity-40"
            >
              {retrying ? 'Retrying…' : 'Retry'}
            </button>
            {!job?.videoAvailable && (
              <span className="text-xs text-red-300/70">
                Stored video is no longer on disk — pick a new file.
              </span>
            )}
            {retryError && (
              <span className="text-xs text-red-300">{retryError}</span>
            )}
          </div>
        </div>
      )}

      {(running || jobLoading || uploading) && (
        <div className="rounded border border-slate-800 bg-slate-950/40 px-3 py-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-slate-300">{stageLabel[stage] ?? stage}</span>
            {job && (
              <span className="text-xs text-slate-500">
                {(job.progress.bytes / (1024 * 1024)).toFixed(1)} MB
              </span>
            )}
          </div>
          <div className="mt-2 h-1.5 rounded bg-slate-800 overflow-hidden">
            <div
              className="h-full bg-indigo-500 transition-all"
              style={{ width: `${stagePct}%` }}
            />
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="rounded border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
        >
          {running || succeeded ? 'Cancel' : 'Clear'}
        </button>
        <button
          onClick={onStart}
          disabled={!file || uploading || running || succeeded}
          className="rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
        >
          {uploading ? 'Uploading…' : running ? 'Processing…' : 'Process'}
        </button>
      </div>
    </div>
  );
}

function ConflictsList({ conflicts }: { conflicts: ImportPreviewResponse['conflicts'] }) {
  return (
    <section className="rounded border border-amber-900/40 bg-amber-950/20 p-3">
      <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-200">
        <AlertTriangle size={14} /> Conflicts ({conflicts.length})
      </h3>
      <ul className="space-y-1 text-xs text-amber-100">
        {conflicts.map((c, i) => (
          <li key={i}>
            {c.kind === 'inactive_player_applied' && (
              <span>
                <strong>{c.ocrName}</strong> matched to inactive player <em>{c.playerName}</em>
              </span>
            )}
            {c.kind === 'reimport_overwrite' && (
              <span>
                <strong>{c.playerName}</strong> already has a recorded application — re-import will overwrite slot
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

